/**
 * AJAX linked-product flavor switching on PDP.
 * - No full page reload
 * - Locks flavor button order from the first paint so sequence stays stable
 */
(function () {
  if (window.__linkedProductsAjaxInit) return;
  window.__linkedProductsAjaxInit = true;

  const PICKER_SELECTOR = '[data-linked-products-picker]';
  const GRID_SELECTOR = '[data-linked-products-grid]';
  const LINK_SELECTOR = '[data-linked-product-ajax]';

  let isLoading = false;
  let lockedOrder = null;

  function getSectionEl(sectionId) {
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  function readOrder(picker) {
    const grid = picker && picker.querySelector(GRID_SELECTOR);
    if (!grid) return null;
    return Array.from(grid.querySelectorAll('[data-linked-product-id]'))
      .map((el) => el.getAttribute('data-linked-product-id'))
      .filter(Boolean);
  }

  function ensureLockedOrder(picker) {
    if (!lockedOrder || !lockedOrder.length) {
      lockedOrder = readOrder(picker);
    }
    return lockedOrder;
  }

  function applyLockedOrder(picker) {
    const order = ensureLockedOrder(picker);
    const grid = picker && picker.querySelector(GRID_SELECTOR);
    if (!grid || !order || !order.length) return;

    const items = Array.from(grid.querySelectorAll('[data-linked-product-id]'));
    const byId = new Map(
      items.map((el) => [el.getAttribute('data-linked-product-id'), el])
    );

    order.forEach((id) => {
      const el = byId.get(id);
      if (el) {
        grid.appendChild(el);
        byId.delete(id);
      }
    });

    // Any new flavors not in the locked order stay at the end
    byId.forEach((el) => grid.appendChild(el));
  }

  function buildSectionUrl(productUrl, sectionId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function parseSectionFromHtml(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector('.shopify-section') ||
      doc.body.firstElementChild
    );
  }

  function updateDocumentTitle(sectionEl) {
    const titled =
      sectionEl.querySelector('[data-shopify-title]') ||
      sectionEl.querySelector('.product-temp-title') ||
      sectionEl.querySelector('h1');
    const productTitle =
      (titled &&
        (titled.getAttribute('data-shopify-title') ||
          titled.textContent.trim())) ||
      '';
    if (!productTitle) return;

    const parts = document.title.split(/\s+[–-]\s+/);
    if (parts.length > 1) {
      document.title = `${productTitle} – ${parts.slice(1).join(' – ')}`;
    } else {
      document.title = productTitle;
    }
  }

  function syncHeaderBackground(sectionEl) {
    const root = sectionEl.querySelector('[data-product-root]') || sectionEl;
    const bg =
      (root.style && root.style.backgroundColor) ||
      getComputedStyle(root).backgroundColor;
    if (!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') return;

    const header = document.querySelector('header[data-header-variant-colors]');
    if (header) header.style.backgroundColor = bg;
  }

  async function loadLinkedProduct(productUrl, sectionId, { push = true } = {}) {
    if (isLoading || !productUrl || !sectionId) return;

    const currentSection = getSectionEl(sectionId);
    if (!currentSection) {
      window.location.href = productUrl;
      return;
    }

    const currentPicker = currentSection.querySelector(PICKER_SELECTOR);
    ensureLockedOrder(currentPicker);

    isLoading = true;

    try {
      const response = await fetch(buildSectionUrl(productUrl, sectionId));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const newSection = parseSectionFromHtml(html, sectionId);
      if (!newSection) throw new Error('Section not found in response');

      if (window.Alpine && typeof Alpine.destroyTree === 'function') {
        Alpine.destroyTree(currentSection);
      }

      currentSection.querySelectorAll('[x-teleport]').forEach((el) => {
        el.remove();
      });

      currentSection.replaceWith(newSection);

      const newPicker = newSection.querySelector(PICKER_SELECTOR);
      if (newPicker) {
        applyLockedOrder(newPicker);
      }

      if (typeof initTeleport === 'function') {
        initTeleport(newSection);
      }

      updateDocumentTitle(newSection);
      syncHeaderBackground(newSection);

      if (push) {
        history.pushState(
          { linkedProductAjax: true, sectionId },
          '',
          productUrl
        );
      }

      newSection.dispatchEvent(
        new CustomEvent('shapes:section:hasmutated', { bubbles: true })
      );

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('[linked-products-ajax]', error);
      window.location.href = productUrl;
    } finally {
      isLoading = false;
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(LINK_SELECTOR);
    if (!link) return;

    // Allow open-in-new-tab / modified clicks
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank'
    ) {
      return;
    }

    event.preventDefault();

    const picker = link.closest(PICKER_SELECTOR);
    const sectionId =
      (picker && picker.getAttribute('data-section-id')) ||
      (link.closest('.shopify-section') &&
        link.closest('.shopify-section').id.replace('shopify-section-', ''));

    loadLinkedProduct(link.getAttribute('href'), sectionId, { push: true });
  });

  window.addEventListener('popstate', () => {
    const picker = document.querySelector(PICKER_SELECTOR);
    if (!picker) return;

    const sectionId = picker.getAttribute('data-section-id');
    if (!sectionId) return;

    loadLinkedProduct(window.location.href, sectionId, { push: false });
  });

  // Capture initial order on first paint
  window.switchOnDOMContentLoaded(() => {
    const picker = document.querySelector(PICKER_SELECTOR);
    if (picker) ensureLockedOrder(picker);
  });
})();
